import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Body2, Header6, PrimaryTextLink, TextButton, borderRadius, formButton, formPadding, inlineTextIconMargin, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import React, { useMemo } from "react";

export enum LocationPrivacy {
    Everyone,
    Private,
    Specific,
}

export const SaveInLocation: React.FC<{
    hierarchyNames: string[];
    privacy: LocationPrivacy;
    icon: React.ReactNode;
    onViewPermissions: () => void;
    onChangeLocation: () => void;
}> = ({ hierarchyNames, privacy, icon, onViewPermissions, onChangeLocation }) => {
    const translate = useIventisTranslate();

    const descriptionElement: React.ReactNode = useMemo(() => {
        if (privacy === LocationPrivacy.Private) {
            return translate(Content.library.saveLocation.visibleYou);
        }

        if (privacy === LocationPrivacy.Everyone) {
            return translate(Content.library.saveLocation.visibleWholeProject);
        }

        // We want to show the "Team members" substring as a link. This may not work the same in all languages.
        // Look for the translated substring in the whole translated text in order to make it a link
        // If it's not found, just link the whole text, which could happen in some languages in which the sentance doesn't contain the substring "Team members"' isolated translation

        const teamMembersButtonSectionText = translate(Content.library.saveLocation.teamMembers);
        const wholeVisibleTeamMembers = translate(Content.library.saveLocation.visibleOtherTeamMembers);

        if (wholeVisibleTeamMembers.includes(teamMembersButtonSectionText)) {
            const [before, after] = wholeVisibleTeamMembers.split(teamMembersButtonSectionText);
            return (
                <>
                    {before}
                    <PrimaryTextLink onClick={onViewPermissions}>{teamMembersButtonSectionText}</PrimaryTextLink>
                    {after}
                </>
            );
        }

        return <PrimaryTextLink onClick={onViewPermissions}>{wholeVisibleTeamMembers}</PrimaryTextLink>;
    }, [privacy, translate]);

    const hierarchy = useMemo(() => {
        const createText = (text: string) => <Header6 className="node-name">{text}</Header6>;
        if (hierarchyNames.length === 1) {
            return (
                <FlexibleBreadcrumbs>
                    <div className="icon-container">{icon}</div>
                    {createText(hierarchyNames[0])}
                </FlexibleBreadcrumbs>
            );
        }

        const chevron = () => <FontAwesomeIcon style={{ marginLeft: 5, marginRight: 5, marginBottom: -1 }} size="sm" icon={{ prefix: "far", iconName: "chevron-right" }} />;

        const first = hierarchyNames[0];
        const last = hierarchyNames[hierarchyNames.length - 1];
        if (hierarchyNames.length === 2) {
            return (
                <FlexibleBreadcrumbs>
                    <div className="icon-container">{icon}</div>
                    {createText(first)}
                    {chevron()}
                    {createText(last)}
                </FlexibleBreadcrumbs>
            );
        }

        const intermediates = hierarchyNames.slice(1, hierarchyNames.length - 1);

        return (
            <FlexibleBreadcrumbs>
                <div className="icon-container">{icon}</div>
                <div className="first">
                    {createText(first)}
                    {chevron()}
                </div>
                <div className="collapse">
                    {intermediates.map((name) => (
                        <>
                            {createText(name)}
                            {chevron()}
                        </>
                    ))}
                </div>
                {createText(last)}
            </FlexibleBreadcrumbs>
        );
    }, [hierarchyNames, icon]);

    return (
        <StyledContainer>
            <div style={{ padding: formPadding }}>
                {hierarchy}
                <Body2 className="bottom-row">{descriptionElement}</Body2>
            </div>
            <StyledButton onClick={onChangeLocation} data-testId="change-location-button">
                {translate(Content.library2.create_node.change_location)}
            </StyledButton>
        </StyledContainer>
    );
};

const StyledButton = styled(TextButton)`
    width: ${formButton.width};
    height: ${formButton.height};
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.primary};
    margin-right: 20px;
`;

const FlexibleBreadcrumbs = styled.div`
    display: inline-block;
    overflow: hidden;
    width: 100%;

    .first *,
    .collapse * {
        display: inline-block;
    }

    .icon-container {
        display: inline-block;
        margin-right: ${inlineTextIconMargin};
    }

    .first,
    .collapse {
        display: inline-block;
        white-space: nowrap;
    }
`;

const StyledContainer = styled.aside`
    border-radius: ${borderRadius.standard};
    background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.light30};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .node-name {
        display: inline-block;
    }
`;
